import styles from "./inputs.module.css"

import React, {useState, useRef, useEffect} from "react"
import MaskedInput from "react-text-mask"
import createNumberMask from "text-mask-addons/dist/createNumberMask"
import assign from "object-assign"
import cx from "classnames"
import PropTypes from "prop-types"
import * as keys from "shared/input"
import * as str from "shared/string"
import * as money from "shared/money"
import {Tooltip} from "shared/tooltips"
import {Section} from "story/components/shared"
import {BzIconPro} from "story/components/typography"

export const InputSection = ({children, className}) => <Section className={className}>{children}</Section>

export const RadioOption = ({type = "radio", isSelected, onClick, children, isComplete, font, className}) => {
  const isButton = (t) => t === "button"
  return (
    <button className={cx(className, styles[type], isSelected && styles[`selected-${type}`], isComplete && styles["complete"], font)} onClick={isComplete ? null : onClick}>
      {isButton(type) && <div className={styles["ring"]}></div>}
      {isSelected && isButton(type) && <div className={styles["circle"]}></div>}
      <div className={cx(isButton(type) && styles["text"])}>{children}</div>
    </button>
  )
}

RadioOption.propTypes = {
  type: PropTypes.oneOf(["radio", "button"]),
  isSelected: PropTypes.bool,
  isComplete: PropTypes.bool
}

export const RadioMenu = ({children, id, onChange, value, isComplete, type = "radio", size, className}) => {
  const onDefaultChange = () => null
  const renderChildren = () =>
    React.Children.map(children, (option) =>
      React.cloneElement(option, {
        type,
        isSelected: option.props.value === value,
        onClick: (onChange || onDefaultChange).bind(null, option.props.value),
        isComplete: isComplete
      })
    )
  return (
    <div id={id} className={cx(className, styles[`${type}s`], styles[size])}>
      {renderChildren()}
    </div>
  )
}

RadioMenu.propTypes = {
  type: PropTypes.oneOf(["radio", "button"])
}

export const Select = ({onChange, children, label}) => (
  <div className={styles["select"]}>
    <select className={styles["select"]} onChange={onChange} aria-label={label || "Select one"}>
      {children}
    </select>
    <BzIconPro className="bz-right-arrow" />
  </div>
)

// this dropdown is specific to the mad-lib widget
// in shared and exported because it needs to be accessed from the html parsing file
export const Dropdown = ({id, onSelect, dropdownIndex = [], label = "Select an entry"}) => {
  const lookup = id - 1 >= 0 ? id - 1 : 0
  // this seems worrisome... no guarantee the id will be a number?
  const {id: dId, options, correct} = dropdownIndex[lookup]
  return (
    <div className={styles["dropdown"]}>
      <select onChange={(e) => onSelect(e.target.value, correct, dId)} aria-label={label}>
        {options.map((word, idx) => (
          <option key={idx} value={idx === 0 ? null : word}>
            {word}
          </option>
        ))}
      </select>
      <BzIconPro className="bz-right-arrow" />
    </div>
  )
}

export const MatchingDropdown = ({onSelect, options, index, isDisabled, ariaLabel}) => {
  return (
    <div className={cx(styles["matching"], styles["dropdown"])}>
      <select onChange={(e) => onSelect(e.target.value, index)} aria-label={ariaLabel || "Select one"}>
        {options.map(({id, term}, idx) => {
          const disabled = isDisabled(id)
          return (
            <option key={idx} disabled={disabled} value={idx === 0 ? null : id}>
              {term}
            </option>
          )
        })}
      </select>
      <BzIconPro className="bz-right-arrow" />
    </div>
  )
}

const withState = (Component, defaultValue) => {
  return (props) => {
    const {initialValue, onChange} = props
    const [value, setValue] = useState(initialValue || defaultValue)

    const _onChange = (eventOrValue, modifierFn) => {
      // eslint-disable-next-line init-declarations
      let _value
      if (typeof eventOrValue === "object") {
        _value = (modifierFn || ((v) => v))(eventOrValue.target.value)
      } else {
        _value = eventOrValue
      }
      setValue(_value)
      onChange(_value)
    }

    return (
      <Component
        {...Object.assign({}, props, {
          onChange: _onChange,
          value: value
        })}
      />
    )
  }
}

const table = {
  currency: (v) => money.asShort(v * 100),
  percent: (v) => (v * 100).toFixed(2) + "%",
  percentNoDecimals: (v) => Math.round(v * 100) + "%",
  years: (v) => v + " Years"
}

const format = (valueType, value) => (table[valueType] || ((v) => v))(value)

const getValueString = (val, type, second, secondType) => (second ? `${format(type, val)}  (${format(secondType, second)})` : format(type, val))

const Label_ = ({value, valueType, secondaryVal, secondaryValType, inputId, tooltipText, customLabelStyles, customValueStyles, className, children}) => (
  <label className={cx(styles["label"], className)} htmlFor={inputId}>
    <span className={customLabelStyles}>{children}</span>
    {tooltipText && <Tooltip align="-1px">{tooltipText}</Tooltip>}
    <span className={cx(styles["value"], customValueStyles)}>{getValueString(value, valueType, secondaryVal, secondaryValType)}</span>
  </label>
)

export const Range_ = ({
  title,
  min,
  max,
  step,
  value,
  secondaryVal,
  secondaryValType,
  onChange,
  valueType = "currency",
  className,
  tooltipText,
  customStyles,
  customValueStyles,
  customLabelStyles
}) => {
  const inputId = str.toKebab(title)
  return (
    <div className={cx(styles[className], styles["range"], customStyles)}>
      <Label_
        className={styles["range-label"]}
        inputId={inputId}
        value={value}
        secondaryVal={secondaryVal}
        secondaryValType={secondaryValType}
        valueType={valueType}
        tooltipText={tooltipText}
        customLabelStyles={customLabelStyles}
        customValueStyles={customValueStyles}
      >
        {title}
      </Label_>
      <input id={inputId} type="range" onChange={onChange} value={value} min={min} max={max} step={step} />
    </div>
  )
}

export const Range = withState(Range_, 0)

export const CheckboxOption = ({type = "checkbox", isSelected, isDisabled, onClick, children, isComplete, font}) => {
  const isButton = (t) => t === "button"
  return (
    <button
      role="checkbox"
      className={cx(styles[type], isSelected && styles[`selected-${type}`], isDisabled && styles["disabled"], isComplete && styles["complete"], font)}
      onClick={isComplete ? null : onClick}
      disabled={isDisabled}
      aria-checked={isSelected}
    >
      {isButton(type) && <div className={styles["ring"]}></div>}
      {isSelected && isButton(type) && <div className={styles["circle"]}></div>}
      <div className={cx(isButton(type) && styles["text"])}>{children}</div>
    </button>
  )
}

CheckboxOption.propTypes = {
  type: PropTypes.oneOf(["checkbox", "button"]),
  isSelected: PropTypes.bool,
  isComplete: PropTypes.bool
}

export const CheckboxMenu = ({children, onChange, isComplete, type = "checkbox", size}) => {
  const onDefaultChange = () => null
  const renderChildren = () =>
    React.Children.map(children, (option) =>
      React.cloneElement(option, {
        type: type,
        isSelected: option.props.isSelected,
        onClick: (onChange || onDefaultChange).bind(null, option.props.value),
        isComplete: isComplete
      })
    )
  return <div className={cx(styles[`${type}s`], styles[size])}>{renderChildren()}</div>
}

CheckboxMenu.propTypes = {
  type: PropTypes.oneOf(["checkbox", "button"])
}

const valueTypeOptions = {
  "currency": {
    mask: createNumberMask({
      prefix: "$",
      decimalLimit: 0,
      integerLimit: 9
    })
  },
  "currency-with-decimals": {
    mask: createNumberMask({
      prefix: "$",
      allowDecimal: true,
      decimalLimit: 2,
      integerLimit: 9
    })
  },
  "percent": {
    mask: createNumberMask({
      prefix: "",
      suffix: "%",
      allowDecimal: true,
      integerLimit: 5,
      decimalLimit: 3
    })
  },
  "number": {
    mask: createNumberMask({
      prefix: "",
      allowDecimal: true
    })
  },
  "text": {
    mask: false
  }
}

const stripChars = (s) => s.replace(/[$%,]/g, "")
const sanitize = (v) => (v === 0 ? v : v || null)

const modifyOut = {
  "currency": (v) => sanitize(Number(stripChars(v))),
  "currency-with-decimals": (v) => sanitize(parseFloat(stripChars(v))),
  "percent": (v) => sanitize(parseFloat(stripChars(v)) / 100),
  "number": (v) => sanitize(parseFloat(stripChars(v))),
  "text": (v) => v
}

const modifyIn = {
  "currency": (v) => (v ? String(v) : null),
  "currency-with-decimals": (v) => (v ? parseFloat(v) : null),
  "percent": (v) => (v ? v * 100 : null),
  "number": (v) => (v ? parseFloat(v) : null),
  "text": (v) => v
}

const cleanInput = {
  currency: (v) => (Number.isFinite(v) ? Math.trunc(v) : v)
}

const placeholderByType = {
  "currency": "$",
  "currency-with-decimals": "$",
  "percent": "%",
  "number": "",
  "text": ""
}

/* Are you programming a calculator? See InputNoFocus in this file. */

export const Input = ({
  value,
  defaultValue,
  placeholder,
  onChange,
  onKeyPress,
  valueType = "currency",
  size = "regular",
  isComplete = false,
  role,
  id,
  font,
  style,
  type, // keypadType or input type, used to determine the default mobile keyboard
  label
}) => {
  return (
    <MaskedInput
      id={id && id}
      className={cx(isComplete && styles["is-complete"], styles["input"], styles[size], size === "small" && styles["inline"], font, style && style)}
      type={type ? type : valueType !== "text" ? "tel" : "text"}
      defaultValue={(modifyIn[valueType] || ((v) => v))(defaultValue)}
      value={(cleanInput[valueType] || ((v) => v))(value)}
      mask={(valueTypeOptions[valueType] || {mask: false}).mask}
      onKeyPress={keys.onEnterKeyPressThunk(onKeyPress)}
      onChange={(event) => onChange(modifyOut[valueType](event.target.value))}
      placeholder={placeholder || placeholderByType[valueType]}
      disabled={isComplete}
      role={role}
      aria-label={label}
    />
  )
}

Input.propTypes = {
  valueType: PropTypes.oneOf(["currency", "currency-with-decimals", "percent", "number", "text"]),
  size: PropTypes.oneOf(["small", "regular"]),
  placeholder: PropTypes.string
}

/* InputNoFocus is used for Calculators where
 *  no focus should be set on inputs or will cause
 *  the page to scroll to that input. */
export const InputNoFocus = (props) => <Input {...assign({}, props, {noFocus: true})} />

export const Label = ({id, inputId, children, size = "regular", font, style, ...props}) => {
  return (
    <label className={cx(styles["label"], styles[size], font, style)} id={id} htmlFor={inputId && inputId} {...props}>
      {children}
    </label>
  )
}

export const LabelAda = ({id, inputId, label, children, size = "regular", font, className}) => (
  <label className={cx(styles["label"], styles["ada"], styles[size], font, className)} id={id} htmlFor={inputId && inputId}>
    <span>{label}</span>
    {children}
  </label>
)
